<script setup>
import Vue, {computed, inject, ref} from "vue";
import {trackerTypes} from "@/constants/trackers";
import {useStore} from "vue2-helpers/vuex";
import {getNoun} from "@/helpers/string";
import trackersCompaniesService from "@/services/trackers-companies.service";
import ComparisonReportModal from "@/components/trackers/comparisonReport/ComparisonReportModal.vue";
import trackersReportsService from "@/services/trackers-reports.service";
import {download} from "@/helpers/file";
import ComparisonReportResultModal from "@/components/trackers/comparisonReport/ComparisonReportResultModal.vue";
import SimilarStartups from "@/components/startups/SimilarStartups.vue";
import EditDescription from "@/components/trackers/EditDescription.vue";
import trackersService from "@/services/trackers.service";
import SharedEcosystems from "@/components/trackers/SharedEcosystems.vue";
import Export from "@/views/trackers/tracker/Export.vue";
import EditTracker from "@/components/trackers/TrackerProjectEdit.vue";
import Filters from "@/views/trackers/tracker/Filters.vue";

//eslint-disable-next-line
import Teleport from "vue2-teleport";
import SuggestedStartups from "@/components/trackers/SuggestedStartups.vue";

const store = useStore();
const tracker = inject("tracker");
const trackerId = tracker.value.id;
const trackerType = tracker.value.type;
const serverFilters = inject("serverFilters");
const displayType = inject("displayType");
const companiesCount = inject("companiesCount");
const filteredCompaniesCount = inject("filteredCompaniesCount");
const selectedRows = inject("selectedRows");
const selectedAllResults = inject("selectedAllResults");
const setCompaniesListUpdated = inject("setCompaniesListUpdated");
const addStartups = inject("addStartups");
const addEcosystems = inject("addEcosystems");
const trackerInfoChanged = inject("trackerInfoChanged");
const getSharedEcosystems = inject("getSharedEcosystems");
const showSideBar = inject("showSideBar");
const filtersApplied = inject("filtersApplied");

const comparisonReportModal = ref(null);
const comparisonReportResultModal = ref(null);
const similarStartupsModal = ref(null);
const editDescription = ref(null);
const sharedEcosystems = ref(null);
const suggestedStartupsModal = ref(null);
const editTracker = ref(null);
const filters = ref(null);
const suggestedStartupsLoading = ref(false);

const isEnterprise = computed(() => store.getters.isEnterprise);
const showPotentialMatches = computed(() => !store.getters.settings.disable_ai && store.getters.isPremiumEnterprise);
const affectedCompaniesCount = computed(() => filteredCompaniesCount.value || companiesCount.value);

async function removeCompanies() {
    if (selectedAllResults.value) {
        if (
            confirm(
                affectedCompaniesCount.value + " " + ((trackerType === trackerTypes.ECOSYSTEMS) ?
                getNoun('ecosystem', affectedCompaniesCount.value) :
                getNoun('startup', affectedCompaniesCount.value))
                + " will be removed from tracker. Do you want to proceed?"
            )
        ) {
            await trackersCompaniesService.removeAllCompanies(trackerId, serverFilters.value);
            setCompaniesListUpdated();
        }
    } else {
        if (
            confirm(
                selectedRows.value.length + " " + ((trackerType === trackerTypes.ECOSYSTEMS) ?
                    getNoun('ecosystem', selectedRows.value.length) :
                    getNoun('startup', selectedRows.value.length))
                + " will be removed from tracker. Do you want to proceed?"
            )
        ) {
            await trackersCompaniesService.removeCompanies(trackerId, selectedRows.value);
            setCompaniesListUpdated();
        }
    }
}

function checkComparisonReportAccess() {
    if (displayType.value !== 'list') {
        alert("Switch to List view and select at least two startups to compare");
    } else if (selectedRows.value.length < 2) {
        alert("Select at least two startups to compare");
    } else {
        comparisonReportModal.value.open();
    }
}

async function getReport(log) {
    if (log.text === 'Feature Comparison Report') {
        comparisonReportResultModal.value.open(log.id, tracker.value.name);
    } else {
        const {data} = await trackersReportsService.downloadReport(log.id);

        let currentDate = new Date();
        download(data, `${tracker.value.name}-${currentDate.toLocaleDateString()}.pdf`);
    }
}

function share() {
    if (!tracker.value.description) {
        editDescription.value.open();
    } else {
        sharedEcosystems.value.open();
    }
}

async function descriptionChanged(description) {
    await trackersService.update(trackerId, {description});
    tracker.value.description = description;
    share();
}

function trackerShared(count) {
    getSharedEcosystems();

    Vue.notify({
        group: 'app',
        type: 'success',
        duration: 5000,
        text: "Tracker shared with " + count + " " + getNoun('ecosystem', count)
    })
}

function showSuggestedStartups() {
    suggestedStartupsLoading.value = true;
    suggestedStartupsModal.value.open();
}
</script>

<template>
    <div class="tracker-list__actions" :class="{'tracker-list__actions--collapsed': !showSideBar}">
        <div class="page-length" id="page-length"/>

        <div class="dropdown--more">
            <b-dropdown id="dropdown-1" text="More" class="">
                <li class="dropdown--more__content">
                    <ul class="actions-list">
                        <template v-if="!tracker.needs_approval">
                            <li
                                v-if="affectedCompaniesCount && tracker.type !== trackerTypes.ECOSYSTEMS && !store.getters.settings.disable_ai && !store.getters.isFreeEnterprise"
                                class="actions-list__grid"
                            >
                                <a @click="checkComparisonReportAccess">Compare Features</a>
                            </li>

                            <template v-if="selectedRows.length">
                                <li
                                    v-if="tracker.type !== trackerTypes.PUBLIC && tracker.user_permissions.indexOf('tracker-startups-delete') >= 0"
                                    class="actions-list__remove"
                                >
                                    <a @click="removeCompanies">Remove From Tracker</a>
                                </li>
                            </template>

                            <li
                                v-if="tracker.type === trackerTypes.PRIVATE && tracker.user_permissions.includes('tracker-startups-add') && (companiesCount > 0 || showPotentialMatches)"
                                class="actions-list__suggest">
                                <template v-if="suggestedStartupsLoading">
                                    Loading...
                                </template>

                                <b-dropdown v-else text="Suggestions" class="dropdown-classic">
                                    <b-dropdown-item
                                        v-if="companiesCount > 0"
                                        @click.prevent="similarStartupsModal.open(tracker.id, selectedRows)"
                                        class="actions-list__similar"
                                    >
                                        Similar startups
                                    </b-dropdown-item>

                                    <b-dropdown-item
                                        v-if="showPotentialMatches && !selectedRows.length"
                                        @click="showSuggestedStartups"
                                        class="actions-list__suggest"
                                    >
                                        Potential Matches
                                    </b-dropdown-item>
                                </b-dropdown>
                            </li>

                            <template v-if="!selectedRows.length">
                                <li class="actions-list__add"
                                    v-if="(tracker.type === trackerTypes.PRIVATE || tracker.type === trackerTypes.POC) && !tracker.needs_approval
                                            && (tracker.user_permissions.includes('tracker-startups-add') || tracker.user_permissions.includes('tracker-add-ecosystem'))">

                                    <b-dropdown v-if="isEnterprise" text="Add To Tracker" class="dropdown-classic">
                                        <b-dropdown-item
                                            v-if="tracker.user_permissions.includes('tracker-startups-add')"
                                            class="actions-list__add pt-2"
                                            @click="addStartups"
                                        >
                                            Add Startups
                                        </b-dropdown-item>

                                        <b-dropdown-item
                                            v-if="tracker.type === trackerTypes.PRIVATE && tracker.user_permissions.includes('tracker-add-ecosystem')"
                                            class="actions-list__ecosystem pt-2 pb-1"
                                            @click="share"
                                        >
                                            Invite Ecosystems
                                        </b-dropdown-item>
                                    </b-dropdown>

                                    <a v-else @click="addStartups">Add Startups</a>
                                </li>

                                <li class="actions-list__add"
                                    v-if="tracker.type === trackerTypes.ECOSYSTEMS && tracker.user_permissions.includes('tracker-startups-add')">
                                    <a @click="addEcosystems">Add Ecosystems</a>
                                </li>
                            </template>

                            <li class="actions-list__filter" :class="{'actions-list__filter--active': filtersApplied}">
                                <a @click="filters.open()">Filters</a>
                            </li>

                            <Export/>
                        </template>

                        <li v-if="tracker.can_edit" class="actions-list__edit__tracker">
                            <a @click="editTracker.open(tracker.id, tracker.type)">Edit Tracker</a>
                        </li>
                    </ul>

                    <ComparisonReportModal
                        ref="comparisonReportModal"
                        @openReport="getReport"
                    />

                    <ComparisonReportResultModal ref="comparisonReportResultModal"/>

                    <SimilarStartups
                        ref="similarStartupsModal"
                        :for-tracker="true"
                        @startupsAdded="setCompaniesListUpdated"
                    />

                    <Teleport to="body">
                        <EditTracker
                            ref="editTracker"
                            @trackerSaved="trackerInfoChanged"
                        />
                    </Teleport>

                    <Filters
                        ref="filters"
                    />

                    <template v-if="isEnterprise">
                        <EditDescription
                            :id="trackerId"
                            ref="editDescription"
                            @descriptionChanged="descriptionChanged"
                        />

                        <SharedEcosystems
                            :tracker-id="trackerId"
                            ref="sharedEcosystems"
                            @trackerShared="trackerShared"
                        />

                        <SuggestedStartups
                            v-if="companiesCount !== null"
                            ref="suggestedStartupsModal"
                            @loaded="suggestedStartupsLoading = false"
                        />
                    </template>
                </li>
            </b-dropdown>
        </div>
    </div>
</template>

<style scoped>

</style>
